import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { ProcessResponse } from "../interfaces/process-response.interface";
import { environment } from "../../../environments/environment";
import { SupabaseService } from "./supabase.service";
import { Mockup } from "../interfaces/mockup.interface";

@Injectable({
  providedIn: 'root',
})
export class ConvertService {
  private http = inject(HttpClient);
  private supabaseService = inject(SupabaseService);
  private prefetchCache = new Map<string, Promise<Blob>>();

  public readonly CalErrorTexts: Record<string, string> = {
    "CALERR_00001": "Au moins une erreur qualité a été détectée (30dpi)",
    "CALERR_00002": "Au moins une erreur qualité a été détectée (60dpi)",
    "CALERR_00003": "Au moins une erreur qualité a été détectée (150dpi)",
    "CALERR_00004": "Le fichier a été déformé de plus de 5%",
    "CALERR_00005": "L'orientation du fichier (portrait/paysage) n'est pas celle attendue",
    "CALERR_00006": "L'orientation du fichier (portrait/paysage) n'est pas celle attendue",
    "CALERR_00007": "Présence d'un tracé magenta >95% vérifiez s'il s'agit ou non de la zone visible sur le fichier source pour la supprimer le cas échéant",
    "CALERR_00008": "Le fichier chargé est un pdf multi-pages",
    "CALERR_00010": "Au moins une partie du fichier est en surimpression. Vérifiez le fichier et désactivez la au besoin.",
    "CALERR_00011": "Le fichier comporte des éléments non CMJN (type RVB) qui seront convertis pour l'impression 4 couleurs",
    "CALERR_00012": "Une ou plusieurs police(s) ne sont pas incorporée(s) au fichier. Vectorisation impossible",
    "CALERR_00013": "Au moins un objet DeviceRGB a été détecté et converti en CMJN. Vérifiez la conformité entre le fichier source et le fichier généré, notamment la profondeur des noirs",
    "CALERR_00014": "Repiquage à faire ou à contrôler",
  }

  /**
   * Converts and uploads a given file to the specified server endpoint.
   * @param file - The file data to be uploaded. It can be in the form of a Blob, Uint8Array, or ArrayBuffer.
   * @param mockup The mockup json
   * @param crop Whether to crop the image to remove the header and footer
   * @param signal An AbortSignal to cancel the request.
   * @returns A promise that resolves with the server response upon successful upload.
   */
  async process(file: File, mockup?: Mockup, crop = false, signal?: AbortSignal): Promise<ProcessResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('crop', crop ? 'true' : 'false');
    if (mockup && mockup.script && this.supabaseService.session()) {
      formData.append('script', mockup.script);
      if (mockup.callasVersion) formData.append('callasVersion', String(mockup.callasVersion));
    }

    const response = await fetch(`${environment.filesServiceUrl}/process`, {
      method: 'POST',
      body: formData,
      signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const result: ProcessResponse = await response.json();
    if (result.upload?.image?.url) {
      this.prefetch(result.upload.image.url);
    }
    return result;
  }

  /**
   * Start fetching a URL in the background so it's ready when needed.
   * @param url - The signed URL to prefetch.
   */
  prefetch(url: string): void {
    if (!this.prefetchCache.has(url)) {
      this.prefetchCache.set(url, fetch(url).then(r => r.blob()));
    }
  }

  /**
   * Get a prefetched blob if available, otherwise fetch it.
   * @param url - The signed URL to get.
   */
  async getBlob(url: string): Promise<Blob> {
    const cached = this.prefetchCache.get(url);
    if (cached) {
      this.prefetchCache.delete(url);
      return cached;
    }
    return fetch(url).then(r => r.blob());
  }

  /**
   * Slice a PDF into individual pages, returned as Blobs fetched from signed URLs.
   * @param file - The PDF file to slice
   */
  async slicePdf(file: File): Promise<Blob[]> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await firstValueFrom(
      this.http.post<{ pages: string[] }>(`${environment.filesServiceUrl}/slicePdf`, formData)
    );
    return Promise.all(response.pages.map(url => fetch(url).then(r => r.blob())));
  }

  /**
   * Zip multiple files server-side and return a signed URL for the zip.
   * @param files - Array of { url, filename } to include in the zip
   * @param zipName - Optional name for the zip file
   * @returns A promise that resolves with the signed URL of the zip file.
   */
  async zip(files: { url: string; filename: string }[], zipName?: string): Promise<string> {
    const response = await fetch(`${environment.filesServiceUrl}/zip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files, ...(zipName ? { zipName } : {}) }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const result: { url: string } = await response.json();
    return result.url;
  }

  /**
   * Check callas status
   * @param directoryId - The directory to check
   * @returns A promise that resolves with the server response upon successful upload.
   */
  async check(directoryId: string): Promise<{ batUrl: string | null, errorsUrl: string | null, prodUrl: string | null } | null> {
    const uploadObservable = this.http.get<{ batUrl: string | null, errorsUrl: string | null, prodUrl: string | null } | null>(`${environment.filesServiceUrl}/check/${directoryId}`);
    return await firstValueFrom(uploadObservable);
  }
}
